import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import VestmapBox from '../../VestmapBox';
import VestmapViewer from '../../VestmapViewer';
import CustomVestmapViewer from '../../CustomVestmapViewer';

export default function SharedGroup() {
  const [searchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [currentVestmap, setCurrentVestmap] = useState({});
  const [viewer, setViewer] = useState(false);

  useEffect(() => {
    fetchSharedReports();
  }, []);

  const fetchSharedReports = async () => {
    try {
      const reportIDs = searchParams.get('reportIDs');
      if (!reportIDs) {
        setError('No report IDs provided');
        setLoading(false);
        return;
      }

      const reportIDArray = reportIDs.split(',');
      const fetchedReports = [];

      for (const reportID of reportIDArray) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_NODE_URL}/record/${reportID}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );

          if (response.ok) {
            const report = await response.json();
            fetchedReports.push(report);
          } else {
            console.warn(`Failed to fetch report ${reportID}`);
          }
        } catch (error) {
          console.error(`Error fetching report ${reportID}:`, error);
        }
      }

      setReports(fetchedReports);
    } catch (error) {
      console.error('Error fetching shared reports:', error);
      setError('Failed to load shared reports');
    } finally {
      setLoading(false);
    }
  };

  const handleModalOpen = record => {
    setCurrentVestmap(record);
    setOpenModal(true);
    setViewer(true);
  };

  const closeViewer = state => {
    setViewer(state);
  };

  const handleDeleteVestmap = id => {
    // Disable delete functionality for shared reports
    console.log('Delete disabled for shared reports');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant='h6' color='error'>
          {error}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          The shared reports may have been removed or are no longer available.
        </Typography>
      </Box>
    );
  }

  if (reports.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant='h6' color='text.secondary'>
          No reports found
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          The shared reports may have been removed or are no longer available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Typography
        variant='h4'
        sx={{
          fontFamily: 'Lato',
          fontSize: '2rem',
          fontStyle: 'normal',
          fontWeight: '800',
          lineHeight: '135%',
          letterSpacing: '0.0125rem',
          color: '#1A202C',
          marginBottom: '2rem',
          textAlign: 'center',
        }}
      >
        Shared Reports
      </Typography>

      <Typography
        variant='body1'
        sx={{
          fontFamily: 'Lato',
          fontSize: '1rem',
          color: '#6b7280',
          marginBottom: '2rem',
          textAlign: 'center',
        }}
      >
        {reports.length} report{reports.length !== 1 ? 's' : ''} shared with you
      </Typography>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2rem',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: '1000px',
            margin: '0 auto',
          }}
        >
          {reports.map(report => (
            <VestmapBox
              key={report._id}
              record={report}
              handleModalOpen={handleModalOpen}
              id={report._id}
              handleDeleteVestmap={handleDeleteVestmap}
              isShared={true}
            />
          ))}
        </div>
      </Box>

      {/* Modal for viewing reports */}
      {viewer &&
        (currentVestmap?.isNewSchema ? (
          <CustomVestmapViewer
            open={openModal}
            setOpen={setOpenModal}
            viewerState={closeViewer}
            vestmapData={currentVestmap}
            openSnackbar={() => {}}
            modifyVestmap={() => {}}
            mapsLoading={false}
            setMapsLoading={() => {}}
            arcgisToken={null}
          />
        ) : (
          <VestmapViewer
            open={openModal}
            setOpen={setOpenModal}
            viewerState={closeViewer}
            vestmapData={currentVestmap}
            openSnackbar={() => {}}
            modifyVestmap={() => {}}
          />
        ))}
    </Box>
  );
}
